import { useEffect, useRef, useState } from 'react';
import { CrewRatesCard } from './CrewRatesCard';
import { useOrgRates } from '../../../hooks/useOrgRates';
import { useResidentialFlatRates } from '../../../hooks/useResidentialFlatRates';
import { api } from '../../../lib/api';
import type { Organization, ResourceRate, TravelTimeRate, ResidentialFlatRates, ResidentialFlatRateRoom } from '../../../types/admin';
import type { ItemizationPreference } from '@iomi/shared';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useOrgWarehouses } from '../../../hooks/useOrgWarehouses';
import { PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING, SETUP_EXPLAINERS } from '@iomi/shared';
import {
  QuoteBuildingCard,
  ShiftSettingsCard,
} from '../../admin/QuoteDefaultsPage';
import type { OrgSettingsUpdate } from '../../../hooks/useOrgSettings';

// ── Types ────────────────────────────────────────────────────────────────

export interface ResidentialSettingsData {
  pricingModel: 'flat_rate' | 'hourly_rate';
  // Hourly rate fields
  rates: Record<string, number>;
  useBlendedTruckDriverRate: boolean;
  /** Flat-rate tiers and add-ons as edited on this step; the wizard saves them on Continue. Null until loaded. */
  flatRates: ResidentialFlatRates | null;
  // Quote defaults
  travelTimeRate: TravelTimeRate;
  fixedTravelTimeHours: number;
  quoteValidityDays: number | null;
  localMoveRadiusType: 'distance' | 'time';
  localMoveRadiusValue: number;
  shiftSettings: { name: string; lengthHours: number }[];
  itemizationPreference: ItemizationPreference;
  /** Name of a card still in edit mode (its unsaved changes would be lost on Continue), or null. */
  openEditor: string | null;
}

interface ResidentialSettingsStepProps {
  org: Organization;
  orgId: string;
  onChange: (data: ResidentialSettingsData) => void;
  /** Highlight required fields that block Continue (set after a blocked attempt). */
  showErrors?: boolean;
}

// ── Main component ───────────────────────────────────────────────────────

export function ResidentialSettingsStep({ org, orgId, onChange, showErrors = false }: ResidentialSettingsStepProps) {
  const { rates, loading: ratesLoading } = useOrgRates(orgId);
  const { flatRates: savedFlatRates, loading: flatRatesLoading } = useResidentialFlatRates(orgId);
  // Flat-rate tiers are edited in place and saved by the wizard on Continue,
  // so a price typed here is never lost to an Edit → Save sequence.
  const [flatRates, setFlatRates] = useState<ResidentialFlatRates | null>(null);
  useEffect(() => {
    if (savedFlatRates && !flatRates) setFlatRates(savedFlatRates);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFlatRates]);

  // Pricing model
  const [pricingModel, setPricingModel] = useState<'flat_rate' | 'hourly_rate'>(
    org.residentialPricingModel ?? 'hourly_rate',
  );

  // Rates
  const rateMap = useRef<Record<string, number>>({});
  const [supervisorRate, setSupervisorRate] = useState(0);
  const [moverRate, setMoverRate] = useState(0);
  const [driverRate, setDriverRate] = useState(0);
  const [truckRate, setTruckRate] = useState(0);
  const [truckDriverRate, setTruckDriverRate] = useState(0);
  const [useBlended, setUseBlended] = useState(org.useBlendedTruckDriverRate ?? true);

  // Quote defaults — tracked via a virtual org for the admin card components
  const [virtualOrg, setVirtualOrg] = useState<Organization>({ ...org });
  // Cards left in edit mode: Continue warns instead of silently discarding them.
  const [openEditors, setOpenEditors] = useState<Record<string, boolean>>({});
  const openEditor = Object.keys(openEditors).find(k => openEditors[k]) ?? null;
  const trackEditor = (name: string) => (editing: boolean) =>
    setOpenEditors(prev => ({ ...prev, [name]: editing }));
  // Portal to Portal needs a warehouse to measure travel from; warn where the choice is made.
  const { warehouses, loading: warehousesLoading } = useOrgWarehouses(orgId);

  // Seed rates from loaded data
  useEffect(() => {
    if (rates.length === 0) return;
    const bySlug = Object.fromEntries(rates.map((r: ResourceRate) => [r.slug, r.hourlyRate]));
    rateMap.current = bySlug;
    setSupervisorRate(bySlug.supervisor ?? 0);
    setMoverRate(bySlug.mover ?? 0);
    setDriverRate(bySlug.driver ?? 0);
    setTruckRate(bySlug.truck ?? 0);
    setTruckDriverRate(bySlug.truck_driver ?? 0);
  }, [rates]);

  // Derive values from virtual org for the parent onChange
  const travelTimeRate = virtualOrg.residentialDefaultTravelTimeRate ?? virtualOrg.defaultTravelTimeRate ?? 'one_hour_standard';
  const fixedTravelTimeHours = virtualOrg.residentialFixedTravelTimeHours ?? virtualOrg.fixedTravelTimeHours ?? 1;
  const quoteValidityDays = virtualOrg.residentialDefaultQuoteValidityDays ?? virtualOrg.defaultQuoteValidityDays ?? 30;
  const localRadiusType = virtualOrg.localMoveRadiusType ?? 'distance';
  const localRadiusValue = virtualOrg.localMoveRadiusValue ?? 50;
  const shiftSettings = virtualOrg.residentialShiftSettings ?? [{ name: 'Standard', lengthHours: 8 }];
  const itemization: ItemizationPreference = ((virtualOrg as unknown as Record<string, unknown>).residentialItemizationPreference as ItemizationPreference) ?? 'single_line';

  // Notify parent of all changes
  useEffect(() => {
    onChange({
      pricingModel,
      rates: {
        supervisor: supervisorRate,
        mover: moverRate,
        driver: driverRate,
        truck: truckRate,
        truck_driver: truckDriverRate,
      },
      useBlendedTruckDriverRate: useBlended,
      flatRates,
      travelTimeRate,
      fixedTravelTimeHours,
      quoteValidityDays,
      localMoveRadiusType: localRadiusType,
      localMoveRadiusValue: localRadiusValue,
      shiftSettings,
      itemizationPreference: itemization,
      openEditor,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingModel, supervisorRate, moverRate, driverRate, truckRate, truckDriverRate, useBlended, flatRates,
    travelTimeRate, fixedTravelTimeHours, quoteValidityDays, localRadiusType, localRadiusValue,
    shiftSettings, itemization, openEditor]);

  // Handle saves from admin card components — update virtual org
  async function handleQuoteBuildingSave(data: OrgSettingsUpdate) {
    setVirtualOrg(prev => ({ ...prev, ...data } as Organization));
  }

  async function handleShiftSettingsSave(data: OrgSettingsUpdate) {
    setVirtualOrg(prev => ({ ...prev, ...data } as Organization));
  }

  if (ratesLoading) {
    return <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* ── Pricing Model ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Pricing Model</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            How do you bill for residential moves?
          </p>
        </div>
        <div className="px-4 py-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setPricingModel('hourly_rate')}
              className={`p-4 rounded-xl border-2 text-center transition-all ${
                pricingModel === 'hourly_rate'
                  ? 'border-plum bg-plum/5'
                  : 'border-stone/60 hover:border-stone'
              }`}
            >
              <p className="text-sm font-semibold text-midnight">Hourly Rate</p>
              <p className="text-xs text-slate/70 mt-1">Crew rates × hours</p>
            </button>
            <button
              type="button"
              onClick={() => setPricingModel('flat_rate')}
              className={`p-4 rounded-xl border-2 text-center transition-all ${
                pricingModel === 'flat_rate'
                  ? 'border-plum bg-plum/5'
                  : 'border-stone/60 hover:border-stone'
              }`}
            >
              <p className="text-sm font-semibold text-midnight">Flat Rate</p>
              <p className="text-xs text-slate/70 mt-1">Per bedroom count</p>
            </button>
          </div>
        </div>
      </section>

      {/* ── Crew Rates (hourly only) ── */}
      {pricingModel === 'hourly_rate' && (
        <CrewRatesCard
          type="residential"
          supervisorRate={supervisorRate}
          moverRate={moverRate}
          driverRate={driverRate}
          truckRate={truckRate}
          truckDriverRate={truckDriverRate}
          useBlended={useBlended}
          onSupervisorChange={setSupervisorRate}
          onMoverChange={setMoverRate}
          onDriverChange={setDriverRate}
          onTruckChange={setTruckRate}
          onTruckDriverChange={setTruckDriverRate}
          onBlendedChange={setUseBlended}
          sharedRatesBanner={org.featureCommercialMovingQuotes === true}
          showErrors={showErrors}
        />
      )}

      {/* ── Residential Flat Rates (flat rate only) ── */}
      {pricingModel === 'flat_rate' && (
        <ResidentialFlatRatesCard
          flatRates={flatRates}
          loading={flatRatesLoading && !flatRates}
          onChange={setFlatRates}
          showErrors={showErrors}
        />
      )}

      {/* ── Quote Building (reuses admin component) ── */}
      <QuoteBuildingCard
        type="residential"
        org={virtualOrg}
        onSave={handleQuoteBuildingSave}
        onEditingChange={trackEditor('Quote Building')}
        hints={{
          travelTime: SETUP_EXPLAINERS.travelTime.body,
          localRadius: SETUP_EXPLAINERS.localMoveRadius.body,
          efficiency: SETUP_EXPLAINERS.efficiencyMultiplier.body,
          validity: SETUP_EXPLAINERS.quoteValidity.body,
        }}
      />
      {travelTimeRate === 'portal_to_portal' && !warehousesLoading && warehouses.length === 0 && (
        <div role="alert" className="rounded-xl bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">{PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING}</p>
          <p className="text-xs text-amber-800 mt-1">
            Use Back to add one on the Warehouse &amp; Trucks step, or later under Operations → Manage Warehouses.
          </p>
        </div>
      )}

      {/* ── Shift Settings (reuses admin component) ── */}
      <ShiftSettingsCard
        type="residential"
        org={virtualOrg}
        onSave={handleShiftSettingsSave}
        onEditingChange={trackEditor('Shift Settings')}
        hint={SETUP_EXPLAINERS.shifts.body}
      />

      {/* ── Itemization (set under Quoting → Quote Defaults; explained here) ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5">
          <p className="font-semibold text-small text-midnight">{SETUP_EXPLAINERS.itemization.title}</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Current setting: {itemization === 'single_line' ? 'Single Line' : itemization === 'crew_trucks' ? 'Crew & Trucks' : itemization === 'duration_transit' ? 'Duration & Transit' : 'Flat Rate Add-ons'}
          </p>
          <p className="text-[12px] text-slate leading-snug mt-2">{SETUP_EXPLAINERS.itemization.body}</p>
        </div>
      </section>

      {/* ── Inventory, Rooms & Services ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Inventory, Rooms & Services</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Pre-loaded with IOMI defaults
          </p>
        </div>
        <div className="px-4 py-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-4">
            <p className="text-[12px] text-emerald-800">
              Your residential inventory items, room templates, and scope of services have been
              pre-loaded with IOMI defaults. You can customize these anytime in your Quoting settings.
            </p>
          </div>
          <InventoryCounts orgId={orgId} />
        </div>
      </section>
    </div>
  );
}

// ── Residential Flat Rates Card ─────────────────────────────────────────
// Always editable in the wizard: the step owns the edited tiers and the
// wizard saves them on Continue. Every enabled tier must be priced above $0.

const tierInputCls = `w-full h-11 px-3 rounded-lg border bg-white text-small text-midnight
  focus:outline-none focus:ring-2 focus:ring-plum/20 transition-colors`;

function ResidentialFlatRatesCard({ flatRates, loading, onChange, showErrors }: {
  flatRates: ResidentialFlatRates | null;
  loading: boolean;
  onChange: (next: ResidentialFlatRates) => void;
  showErrors: boolean;
}) {
  function updateRoom(bedroomCount: number, field: keyof ResidentialFlatRateRoom, value: number) {
    if (!flatRates) return;
    onChange({
      ...flatRates,
      rooms: flatRates.rooms.map(r => r.bedroomCount === bedroomCount ? { ...r, [field]: value } : r),
    });
  }

  function updateAddon(slug: string, rate: number) {
    if (!flatRates) return;
    onChange({
      ...flatRates,
      addons: flatRates.addons.map(a => a.slug === slug ? { ...a, flatRate: rate } : a),
    });
  }

  if (loading) {
    return (
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Residential Flat Rates</p>
        </div>
        <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
      </section>
    );
  }

  if (!flatRates) {
    return (
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Residential Flat Rates</p>
        </div>
        <div className="px-4 py-4">
          <p className="text-[13px] text-slate italic">No flat rate data available.</p>
        </div>
      </section>
    );
  }

  const enabledRooms = flatRates.rooms.filter(r => r.enabled);

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone">
        <p className="font-semibold text-small text-midnight">Residential Flat Rates</p>
        <p className="text-[12px] text-slate leading-snug mt-0.5">
          One price per bedroom count. Every enabled tier needs a price above $0; a $0 tier produces a $0 quote.
        </p>
      </div>

      <div className="px-4 py-4 space-y-4">
        {enabledRooms.length === 0 && (
          <p className={`text-[13px] ${showErrors ? 'text-rose' : 'text-slate'}`}>
            No bedroom tiers are enabled. Enable tiers under Quoting → Quote Defaults after setup, or switch to hourly pricing.
          </p>
        )}

        {enabledRooms.length > 0 && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
              Room-Based Rates
            </p>
            <div className="space-y-3">
              {enabledRooms.map((room) => {
                const invalid = showErrors && !(room.flatRate > 0);
                return (
                  <div
                    key={room.bedroomCount}
                    className={`rounded-lg border p-3 space-y-2 ${invalid ? 'border-rose' : 'border-stone'}`}
                  >
                    <p className="text-[13px] font-semibold text-midnight">
                      {room.label || `${room.bedroomCount} BR`}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-medium text-slate mb-1">Flat Rate ($)</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="0"
                          value={room.flatRate > 0 ? room.flatRate : ''}
                          onChange={e => updateRoom(room.bedroomCount, 'flatRate', parseFloat(e.target.value) || 0)}
                          aria-invalid={invalid || undefined}
                          className={`${tierInputCls} ${invalid ? 'border-rose' : 'border-stone focus:border-plum'}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate mb-1">Duration (hrs)</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.5"
                          value={room.jobDurationHours ?? ''}
                          onChange={e => updateRoom(room.bedroomCount, 'jobDurationHours', parseFloat(e.target.value) || 0)}
                          className={`${tierInputCls} border-stone focus:border-plum`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate mb-1">Crew Size</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={room.crewSize ?? ''}
                          onChange={e => updateRoom(room.bedroomCount, 'crewSize', parseInt(e.target.value) || 0)}
                          className={`${tierInputCls} border-stone focus:border-plum`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate mb-1">Trucks</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={room.trucks ?? ''}
                          onChange={e => updateRoom(room.bedroomCount, 'trucks', parseInt(e.target.value) || 0)}
                          className={`${tierInputCls} border-stone focus:border-plum`}
                        />
                      </div>
                    </div>
                    {invalid && <p className="text-[12px] text-rose">Required. Enter a flat rate above $0.</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {flatRates.addons.length > 0 && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
              Add-Ons
            </p>
            <div className="space-y-2">
              {flatRates.addons.map((addon) => (
                <div key={addon.slug} className="flex items-center justify-between gap-3">
                  <span className="text-[13px] text-midnight flex-1">{addon.label}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[13px] text-slate">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={addon.flatRate}
                      onChange={e => updateAddon(addon.slug, parseFloat(e.target.value) || 0)}
                      className={`w-24 h-11 px-3 rounded-lg border border-stone bg-white text-small text-midnight text-right
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Inventory counts ─────────────────────────────────────────────────────

function InventoryCounts({ orgId }: { orgId: string }) {
  const [counts, setCounts] = useState<{ rooms: number; items: number; services: number } | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ success: boolean; data: unknown[] }>(`/org-admin/${orgId}/residential-rooms`).then(r => r.data.length).catch(() => 0),
      api.get<{ success: boolean; data: unknown[] }>(`/org-admin/${orgId}/inventory`).then(r => r.data.length).catch(() => 0),
      api.get<{ success: boolean; data: unknown[] }>(`/org-admin/${orgId}/services`).then(r => r.data.length).catch(() => 0),
    ]).then(([rooms, items, services]) => setCounts({ rooms, items, services }));
  }, [orgId]);

  if (!counts) return null;

  return (
    <div className="flex gap-4 text-center">
      <div className="flex-1 py-2 rounded-lg bg-mist">
        <p className="text-lg font-semibold text-midnight">{counts.rooms}</p>
        <p className="text-[11px] text-slate">Rooms</p>
      </div>
      <div className="flex-1 py-2 rounded-lg bg-mist">
        <p className="text-lg font-semibold text-midnight">{counts.items}</p>
        <p className="text-[11px] text-slate">Items</p>
      </div>
      <div className="flex-1 py-2 rounded-lg bg-mist">
        <p className="text-lg font-semibold text-midnight">{counts.services}</p>
        <p className="text-[11px] text-slate">Services</p>
      </div>
    </div>
  );
}
